import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, Loader2, MessageSquare, Stethoscope, AlertCircle, MapPin, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import InstallNotifyGate from '@/components/checkin/InstallNotifyGate';

/**
 * ClinicCheckinPage — `/dr/:slug/checkin?loc=<location_id>`
 *
 * Mobile-first patient check-in:
 *   1. Patient enters mobile → backend lookup against doctor + clinic.
 *      Returning patient → fast-path: just pick reason + symptom, tap Check in.
 *      New patient → continue with name + gender.
 *   2. Final fields: reason (Consult / Mini-procedure / Surgery) + symptom (optional).
 *   3. Submit creates the visit row + fires the TQS feedback SMS.
 */
const GENDER_PILLS = [
  { v: 'female', label: 'Female' },
  { v: 'male', label: 'Male' },
  { v: 'other', label: 'Other' },
];

const REASON_PILLS = [
  { v: 'consult', label: 'Consult' },
  { v: 'miniprocedure', label: 'Mini-procedure' },
  { v: 'surgery', label: 'Surgery' },
];

export default function ClinicCheckinPage() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const locId = (search.get('loc') || '').trim();

  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mobile-first flow state
  const [phone, setPhone] = useState('');
  const [phoneChecked, setPhoneChecked] = useState(false); // becomes true after lookup
  const [returningPatient, setReturningPatient] = useState(null); // { patient_name, patient_gender } or null
  const [checkingPhone, setCheckingPhone] = useState(false);

  // Patient identity (filled either by lookup or by typing) — structured
  // into First / Middle / Surname so the Patient Log carries the full
  // proper name (no casual single-word entries like "Aeasha").
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [surname, setSurname] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');

  // Visit details
  const [reason, setReason] = useState('consult');
  const [symptom, setSymptom] = useState('');
  // PDP lock — true when the symptom was previously entered by the patient
  // (online booking notes OR earlier clinic check-in). UI renders it
  // read-only with a small "Patient-Data · locked" chip.
  const [symptomLocked, setSymptomLocked] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const nameRef = useRef(null);

  // Install + Allow-Notifications gate — runs once between the phone
  // lookup and the rest of the form. Skipped on subsequent edits if the
  // patient already saw it, or if they explicitly tap "Skip for now".
  // sessionStorage-keyed so a misclick doesn't re-prompt during the same
  // visit, but a fresh QR scan tomorrow will show it again.
  const [gatePassed, setGatePassed] = useState(false);
  const gateKey = `docsite_install_gate_${slug}`;
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const seen = sessionStorage.getItem(gateKey);
      if (seen) setGatePassed(true);
    }
  }, [gateKey]);
  // Move focus to the first-name input for new patients
  const [dpdPending, setDpdPending] = useState(false);
  const handleGateContinue = () => {
    try { sessionStorage.setItem(gateKey, '1'); } catch { /* private mode */ }
    setGatePassed(true);
  };

  // Derived "first middle surname" used for the returning-patient banner
  // and as the canonical patient_name sent to the server.
  const fullName = useMemo(() => {
    return [firstName, middleName, surname].map(s => (s || '').trim()).filter(Boolean).join(' ');
  }, [firstName, middleName, surname]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/doctor/${slug}`)
      .then(r => { if (!cancelled) { setDoctor(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setLoading(false); } });
    return () => { cancelled = true; };
  }, [slug]);

  const location = useMemo(() => {
    if (!doctor || !locId) return null;
    return (doctor.locations || []).find(l => l.id === locId) || null;
  }, [doctor, locId]);

  // Doctor's name in possessive form for the header: "Dr Chetna Tikoo's"
  const doctorPossessive = useMemo(() => {
    if (!doctor?.name) return '';
    return `${doctor.name}'s`;
  }, [doctor?.name]);

  const lookupPhone = async () => {
    setErr('');
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) { setErr('Please enter a 10-digit mobile number.'); return; }
    setCheckingPhone(true);
    try {
      const { data } = await api.get(`/public/dr/${slug}/checkin/lookup`, {
        params: { phone: digits, loc: locId || '' },
      });
      setPhoneChecked(true);
      // Auto-skip the Install + Notify gate when this phone already has a
      // live web-push subscription against this doctor. The subscription
      // row in `chat_push_subscriptions` can only exist if the patient
      // previously completed BOTH install + permission-grant — so it is
      // self-sufficient proof. We do NOT require the PWA to be open in
      // standalone mode (QR scans usually open Chrome/Safari first, not
      // the installed PWA).
      if (data?.has_push_subscription) {
        try { sessionStorage.setItem(gateKey, '1'); } catch { /* private mode */ }
        setGatePassed(true);
      }
      // DpD-pending: the doctor pre-recorded this number from the dashboard
      // but the patient hasn't self-verified yet. Treat the form as fresh
      // onboarding (no auto-fill) and surface the soft banner downstream
      // (state below). Even if `data.found` is true, we deliberately do
      // NOT pre-fill name/gender — the patient owns the identity write.
      if (data?.dpd_pending_patient_verification) {
        setDpdPending(true);
        setReturningPatient(null);
        setTimeout(() => nameRef.current?.focus?.(), 50);
        return;
      } else {
        setDpdPending(false);
      }
      if (data?.found) {
        setReturningPatient({ patient_name: data.patient_name || '', patient_gender: data.patient_gender || '' });
        // Prefer the structured parts when the server has them; fall back
        // to a conservative split of the legacy combined name so older
        // records still pre-fill cleanly.
        const f = (data.first_name || '').trim();
        const m = (data.middle_name || '').trim();
        const s = (data.surname || '').trim();
        if (f || m || s) {
          setFirstName(f);
          setMiddleName(m);
          setSurname(s);
        } else {
          const parts = (data.patient_name || '').trim().split(/\s+/).filter(Boolean);
          if (parts.length === 1) setFirstName(parts[0]);
          else if (parts.length === 2) { setFirstName(parts[0]); setSurname(parts[1]); }
          else if (parts.length >= 3) { setFirstName(parts[0]); setMiddleName(parts.slice(1, -1).join(' ')); setSurname(parts[parts.length - 1]); }
        }
        setGender(data.patient_gender || '');
        // Pre-fill the symptom from the last visit OR the online booking the
        // patient made with this number. Locked (read-only) when the value
        // is patient-entered PDP — sealed medical record per founder rule.
        if (data.primary_symptom) setSymptom(data.primary_symptom);
        setSymptomLocked(!!data.primary_symptom_locked);
      } else {
        setReturningPatient(null);
        setSymptomLocked(false);
        // Move focus to the first-name input for new patients
        setTimeout(() => nameRef.current?.focus?.(), 50);
      }
    } catch (e) {
      // Soft-fail: still let the patient continue as a new check-in.
      setPhoneChecked(true);
      setReturningPatient(null);
    } finally {
      setCheckingPhone(false);
    }
  };

  // Best-effort device geolocation (never blocks check-in). Resolves within
  // ~4s or returns null. Captured for the doctor-side audit log only.
  const getGeo = () => new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null);
    let settled = false;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    const t = setTimeout(() => done(null), 4000);
    navigator.geolocation.getCurrentPosition(
      (pos) => { clearTimeout(t); done({ lat: pos.coords.latitude, lng: pos.coords.longitude, geo_accuracy: pos.coords.accuracy }); },
      () => { clearTimeout(t); done(null); },
      { enableHighAccuracy: false, timeout: 3500, maximumAge: 60000 },
    );
  });

  const submit = async () => {
    setErr('');
    const trimFirst = (firstName || '').trim();
    const trimMid = (middleName || '').trim();
    const trimSur = (surname || '').trim();
    const digits = phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) { setErr('Please enter a 10-digit mobile number.'); return; }
    if (trimFirst.length < 2) { setErr('First name is required (min 2 letters).'); return; }
    if (!gender) { setErr('Please select your gender.'); return; }
    if (!reason) { setErr('Please pick the reason for your visit.'); return; }
    const composedFull = [trimFirst, trimMid, trimSur].filter(Boolean).join(' ');
    setSubmitting(true);
    const geo = await getGeo();
    try {
      const { data } = await api.post(`/public/dr/${slug}/checkin`, {
        patient_name: composedFull,
        first_name: trimFirst,
        middle_name: trimMid,
        surname: trimSur,
        phone: digits,
        gender,
        age: age ? parseInt(age, 10) : null,
        symptom: (symptom || '').trim(),
        reason,
        location_id: locId,
        lat: geo?.lat ?? null,
        lng: geo?.lng ?? null,
        geo_accuracy: geo?.geo_accuracy ?? null,
      });
      setDone(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Sorry, something went wrong. Please ask the receptionist.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-pulse text-slate-500 text-sm">Loading…</div></div>;
  }
  if (!doctor) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <AlertCircle size={36} className="text-amber-500 mx-auto mb-3" />
          <p className="text-slate-700 mb-2">This clinic check-in page isn't available.</p>
          <p className="text-xs text-slate-500">Please ask the receptionist for help.</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50/60 via-white to-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <Helmet><title>{`Checked in · ${doctor.name}`}</title><meta name="robots" content="noindex" /></Helmet>
        <div className="max-w-md mx-auto px-5 pt-14 pb-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
            <CheckCircle2 size={32} className="text-emerald-600" strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>You're checked in</h1>
          <p className="text-sm text-slate-600 mb-1">
            {doctor.name} will see your details right away.
          </p>
          {location && (
            <p className="text-xs text-emerald-700 flex items-center gap-1 justify-center mb-6"><MapPin size={11} /> {location.name}</p>
          )}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4 text-left">
            <div className="flex items-start gap-2 mb-3">
              <MessageSquare size={16} className="text-sky-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">Before you leave today</p>
                <p className="text-xs text-slate-600 mt-1">
                  Tap the <b>3-question feedback link</b> below ({"<"}30 seconds). On day 7 you'll get a free in-app reminder for the final outcome question — no SMS needed.
                </p>
              </div>
            </div>
            {done.tqs_url && (
              <Link
                to={done.tqs_url}
                className="block w-full bg-sky-600 hover:bg-sky-700 text-white rounded-xl px-4 py-3 text-center text-sm font-semibold mt-1"
                data-testid="checkin-tqs-link"
              >
                Take the 30-second survey now →
              </Link>
            )}
          </div>
          {done.duplicate ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              You've already checked in today — the receptionist has your name.
            </p>
          ) : null}
          <p className="text-[10px] text-slate-400 mt-10">Powered by DocSite&trade;</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/60 via-white to-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet><title>{`Self check-in · ${doctor.name}`}</title><meta name="robots" content="noindex" /></Helmet>
      <div className="max-w-md mx-auto px-5 pt-10 pb-16">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border border-sky-200 mb-3 shadow-sm">
            <Stethoscope size={26} className="text-sky-600" />
          </div>
          <p className="text-xs uppercase tracking-wider font-semibold text-sky-700 mb-1">Welcome</p>
          <h1
            className="text-2xl font-semibold text-slate-900"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="checkin-doctor-name"
          >
            {doctorPossessive}
          </h1>
          {doctor.affiliations && (
            <p className="text-sm text-slate-700 font-medium mt-1" data-testid="checkin-doctor-affiliations">{doctor.affiliations}</p>
          )}
          <p
            className="text-[13px] sm:text-sm font-extrabold uppercase tracking-[0.14em] text-sky-700 mt-3"
            data-testid="checkin-page-tagline"
          >
            Patient Quick Self-Checkin @ Clinic
          </p>
          {location ? (
            <p className="text-sm text-slate-700 mt-2 flex items-center justify-center gap-1.5" data-testid="checkin-location-name">
              <MapPin size={13} className="text-emerald-600" /> {location.name}
            </p>
          ) : (
            <p className="text-sm text-slate-600 mt-2">Please share your details to register today's visit.</p>
          )}
          {/* ── Same-day check-in advisory (Feb 2026) ─────────────────────
              Founder rule: DHF and Digital Pharmacy Prescription can ONLY
              be generated on the same calendar day as the visit. Loud
              bullet-list callout so returning-home patients don't scan
              this QR days later and expect a back-dated prescription. */}
          <div
            className="mt-3 mx-auto max-w-sm text-left rounded-xl bg-amber-50 border border-amber-300 px-3.5 py-3 shadow-sm"
            data-testid="checkin-same-day-advisory"
          >
            <p className="text-[11px] sm:text-xs font-extrabold uppercase tracking-wider text-amber-800 mb-1.5">
              Check in TODAY — before you leave the clinic
            </p>
            <ul className="text-[12.5px] sm:text-sm text-amber-900 leading-snug space-y-1 list-disc pl-4">
              <li>Unlocks your <b>Digital Health File (DHF)</b> — free, only for same-day check-ins.</li>
              <li>Unlocks your <b>Digital Pharmacy Prescription (DPP)</b> — signed &amp; verifiable.</li>
              <li>Lets DocSite <b>monitor your health progress</b> over time.</li>
              <li>Late check-ins save your visit history only — <b>no DHF, no prescription</b>.</li>
            </ul>
          </div>
          <p className="text-[12px] text-emerald-800 mt-2 leading-tight" data-testid="checkin-dhf-benefit">
            <span className="font-extrabold uppercase tracking-wide">DIGITAL HEALTH FILE</span>
            <span className="font-medium"> for </span><span className="font-bold">same day</span><span className="font-medium"> CHECK-IN only. </span><span className="font-extrabold">FREE.</span>
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
          {/* Step 1 — Mobile number (always first). */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Mobile number</label>
            <div className="flex gap-2">
              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setPhone(v);
                  // If the patient edits the number after a lookup, reset state.
                  if (phoneChecked) {
                    setPhoneChecked(false);
                    setReturningPatient(null);
                    setFirstName('');
                    setMiddleName('');
                    setSurname('');
                    setGender('');
                    setAge('');
                  }
                }}
                placeholder="10-digit mobile"
                maxLength={10}
                className="flex-1 rounded-xl border border-slate-300 px-3.5 py-3 text-base font-mono tracking-wider focus:ring-2 focus:ring-sky-500/30 outline-none"
                data-testid="checkin-phone-input"
              />
              {!phoneChecked && (
                <button
                  type="button"
                  onClick={lookupPhone}
                  disabled={phone.length !== 10 || checkingPhone}
                  className="px-4 rounded-xl bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-1.5"
                  data-testid="checkin-phone-continue"
                >
                  {checkingPhone ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  Enter
                </button>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">
              We'll show your feedback link on this screen — no SMS, no missed calls.
            </p>
          </div>

          {/* Step 1.5 — Install + Allow Notifications gate (runs once per
              QR session). Renders the moment the mobile-number lookup
              completes — BEFORE identity, gender, reason, or symptom
              fields appear. This is the "your symptoms will be tracked
              till healed" moment that converts the patient into a
              push-reachable contact for the day-7 outcome question. */}
          {phoneChecked && !gatePassed && (
            <InstallNotifyGate
              phone={phone}
              doctorSlug={slug}
              doctorName={doctor?.name || ''}
              onContinue={handleGateContinue}
            />
          )}

          {/* DpD-pending soft banner — surfaces only when the doctor has
              pre-recorded this number from the dashboard but the patient
              has not yet self-verified via QR. Wording is deliberately
              patient-friendly (no medical-record language) — frames the
              flow as a benefit ("automatic digital health file") rather
              than a tracking notice. */}
          {phoneChecked && gatePassed && dpdPending && (
            <div className="rounded-xl bg-sky-50 border border-sky-200 px-3.5 py-3 flex items-start gap-2" data-testid="checkin-dpd-banner">
              <CheckCircle2 size={16} className="text-sky-600 mt-0.5 shrink-0" />
              <p className="text-[12.5px] text-sky-900 leading-relaxed">
                You have visited this doctor prior. Please enter your details to receive your digital health file for subsequent visits automatically.
              </p>
            </div>
          )}

          {/* Step 2 — Returning patient banner OR new-patient identity fields. */}
          {phoneChecked && gatePassed && !dpdPending && returningPatient && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3.5 py-3 flex items-start gap-2" data-testid="checkin-returning-banner">
              <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-emerald-900">Welcome back, {fullName || returningPatient.patient_name || 'patient'}</p>
                <p className="text-[11px] text-emerald-800/80 mt-0.5">We've matched your number on file. Pick the reason below and check in.</p>
              </div>
            </div>
          )}

          {phoneChecked && gatePassed && (dpdPending || !returningPatient) && (
            <>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">
                  First name <span className="text-rose-600">*</span>
                </label>
                <input
                  ref={nameRef}
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="e.g. Amolika"
                  className="w-full rounded-xl border border-slate-300 px-3.5 py-3 text-base focus:ring-2 focus:ring-sky-500/30 outline-none"
                  data-testid="checkin-first-name-input"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Middle name <span className="text-slate-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={middleName}
                    onChange={(e) => setMiddleName(e.target.value)}
                    placeholder="e.g. Devi"
                    className="w-full rounded-xl border border-slate-300 px-3.5 py-3 text-base focus:ring-2 focus:ring-sky-500/30 outline-none"
                    data-testid="checkin-middle-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Surname <span className="text-amber-600 font-medium">(please share)</span></label>
                  <input
                    type="text"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    placeholder="e.g. Mukherjee"
                    className="w-full rounded-xl border border-slate-300 px-3.5 py-3 text-base focus:ring-2 focus:ring-sky-500/30 outline-none"
                    data-testid="checkin-surname-input"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500 -mt-1 leading-snug">
                Your doctor would love to address you by your full name on file. Sharing your surname helps with proper records.
              </p>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Gender</label>
                <div className="grid grid-cols-3 gap-2" data-testid="checkin-gender-pills">
                  {GENDER_PILLS.map(g => (
                    <button
                      key={g.v}
                      type="button"
                      onClick={() => setGender(g.v)}
                      className={`px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors border ${gender === g.v ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-300 hover:border-sky-300'}`}
                      data-testid={`checkin-gender-${g.v}`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Age — always shown (even for returning patients) so they can
              fill it when missing. Never PDP-locked. */}
          {phoneChecked && gatePassed && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Age <span className="text-slate-400 font-normal">(years)</span></label>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={120}
                value={age}
                onChange={(e) => setAge(e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="e.g. 34"
                className="w-full rounded-xl border border-slate-300 px-3.5 py-3 text-base focus:ring-2 focus:ring-sky-500/30 outline-none"
                data-testid="checkin-age-input"
              />
            </div>
          )}

          {/* Step 3 — Visit details (reason + symptom). Only after phone lookup. */}
          {phoneChecked && gatePassed && (
            <>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Reason for visit</label>
                <div className="grid grid-cols-3 gap-2" data-testid="checkin-reason-pills">
                  {REASON_PILLS.map(r => (
                    <button
                      key={r.v}
                      type="button"
                      onClick={() => setReason(r.v)}
                      className={`px-2.5 py-2.5 rounded-xl text-[13px] font-semibold transition-colors border ${reason === r.v ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-700 border-slate-300 hover:border-sky-300'}`}
                      data-testid={`checkin-reason-${r.v}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 flex items-center gap-1.5">
                  <span>What's bothering you? <span className="text-slate-400 font-normal">(optional)</span></span>
                  {symptomLocked && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200"
                      title="You shared this with the doctor earlier — sealed on your record."
                      data-testid="checkin-symptom-locked-chip"
                    >
                      Patient-Data · locked
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={symptom}
                  onChange={(e) => setSymptom(e.target.value)}
                  placeholder="e.g. cough for 5 days, ear pain"
                  maxLength={200}
                  readOnly={symptomLocked}
                  className={`w-full rounded-xl border border-slate-300 px-3.5 py-3 text-base focus:ring-2 focus:ring-sky-500/30 outline-none ${symptomLocked ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''}`}
                  data-testid="checkin-symptom-input"
                />
              </div>
            </>
          )}

          {err && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2" data-testid="checkin-error">
              {err}
            </div>
          )}

          {phoneChecked && gatePassed && (
            <button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white rounded-xl px-4 py-3.5 text-sm font-semibold flex items-center justify-center gap-2"
              data-testid="checkin-submit-btn"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              {submitting ? 'Checking you in…' : 'Check in'}
            </button>
          )}

          <p className="text-[10px] text-slate-400 text-center leading-relaxed" data-testid="checkin-consent-line">
            Submitting lets {doctor.name.split(' ')[0]} contact you about your care. Your number is private and never shared. You will now receive on this number your digital health files, data and certificates from your doctor based on this consent.
          </p>
        </div>

        <p className="text-center text-[10px] text-slate-400 mt-8">Powered by DocSite&trade;</p>
      </div>
    </div>
  );
}
